import { CommonModule } from '@angular/common';
import type { OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { Component, Input, inject } from '@angular/core';
import type { SafeHtml } from '@angular/platform-browser';
import { EnvService } from '@core-mkt/services/env/env.service';
import type { ComponentTheme, ThemeInterface } from '@core-mkt/services/theme-parser/theme-parser.service';
import { ThemeParserService } from '@core-mkt/services/theme-parser/theme-parser.service';
import { WebinarScheduleService } from '@core-mkt/services/webinar-schedule/webinar-schedule.service';
import type { XGritCompleteProduct } from '@core-mkt/services/xgrit-api/xgrit-product';
import { ProductPillComponent } from '@core-mkt/shared/components/product-pill/product-pill.component';
import { ProductPillService } from '@core-mkt/shared/components/product-pill/product-pill.service';
import { TierBadgeComponent } from '@core-mkt/shared/components/tier-badge/tier-badge.component';
import type { VisionButton } from '@core-mkt/shared/components/vision-button/vision-button/vision-button';
import { VisionModalService } from '@core-mkt/shared/components/vision-modal/vision-modal.service';
import type { WebinarScheduleResponse } from '@core-mkt/shared/components/webinar-schedule/webinar-schedule';
import { CustomDecimalPipe } from '@ecom/common';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faStar as farStar, faStar as fasStar } from '@fortawesome/free-solid-svg-icons';
import { VisionButtonComponent } from '../../../vision-button/vision-button/vision-button.component';
import { ProductItem } from '../product-card';

@Component({
  selector: 'card-item',
  templateUrl: './card-item.component.html',
  styleUrls: ['./card-item.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    FontAwesomeModule,
    VisionButtonComponent,
    CustomDecimalPipe,
    ProductPillComponent,
    TierBadgeComponent,
  ],
})
export class CardItemComponent implements OnInit, OnChanges {
  @Input() data: ProductItem;
  @Input() ctaType: string;
  @Input() hideCta: boolean;
  @Input() learnMore: boolean;
  @Input() hasMultipleWebinars: boolean;
  @Input() filterType: boolean;
  theme: ThemeInterface;
  product: XGritCompleteProduct;
  productSavings = '0';
  fasStar = fasStar;
  farStar = farStar;
  faStarStyles = { width: '13px', color: '#fcb11a' };
  priceText = '';
  altPriceText = '';

  private readonly pillService = inject(ProductPillService);

  constructor(
    private themeParser: ThemeParserService,
    private env: EnvService,
    private modal: VisionModalService,
    private webinarScheduleService: WebinarScheduleService,
  ) {}

  get isAceDesign(): boolean {
    return (this.env.get.brandConfig.designMode ?? 'ace') === 'ace';
  }

  get cardBorderClass(): string {
    return this.pillService.getCardBorderClass(this.data?.productEntry?.[0]?.productPills);
  }

  get viewScheduleLabel(): string {
    return this.hasMultipleWebinars ? 'View Schedule' : '';
  }

  get ctaLabel(): string {
    return this.data.ctaLabel ? this.data.ctaLabel : this.env.get.brandConfig.ctaText;
  }

  get checkStyle(): string {
    return this.env.get.brandConfig.checkClassName;
  }

  get showFee(): boolean {
    return (
      this.env.get.brandConfig.showFee &&
      !this.data?.suppressFee &&
      this.product &&
      this.product.apiData.productFees > 0
    );
  }

  get feeLabel(): string {
    return this.env.get.brandConfig.feeLabel;
  }

  get ctaLink(): string | undefined {
    if (this.data.ctaLink) {
      return this.data.ctaLink;
    } else {
      if (this.product) {
        return this.product.apiData.checkoutLink;
      } else {
        return undefined;
      }
    }
  }

  get ctaLinkAriaLabel(): string | undefined {
    if (this.data.ctaLink) {
      return undefined;
    } else {
      if (this.product) {
        return this.product.apiData.title;
      } else {
        return undefined;
      }
    }
  }

  get isLearnMoreIncluded(): boolean {
    return this.learnMore;
  }

  get showCTA(): boolean {
    return this.ctaLabel && this.ctaLink && !this.hideCta;
  }

  get disclaimer(): string {
    return this.product && this.product.craftData.disclaimer;
  }

  get showDisclaimer(): boolean {
    const disclaimer = this.product && this.product.craftData.disclaimer;
    return (
      this.product && this.product.craftData && disclaimer !== null && disclaimer !== undefined && disclaimer.length > 0
    );
  }

  get ctaNewTab(): boolean {
    return this.data.ctaNewTab;
  }

  get ctaNofollow(): boolean {
    return this.data.ctaNofollow;
  }

  get lmLabel(): string {
    return this.data.learnMoreLabel !== null && this.data.learnMoreLabel.length > 0
      ? this.data.learnMoreLabel
      : 'Learn More';
  }

  get lmLink(): string | undefined {
    return this.data.learnMoreLink !== null && this.data.learnMoreLink.length > 0 ? this.data.learnMoreLink : undefined;
  }

  get lmNewTab(): boolean {
    return this.data.learnMoreNewTab;
  }

  get lmNofollow(): boolean {
    return this.data.learnMoreNofollow;
  }

  get salePrice(): string {
    return '';
  }

  get savings(): string {
    return this.product ? this.productSavings : '0';
  }

  get showSavings(): boolean {
    return this.data.showSavings && parseInt(this.savings) !== 0;
  }

  get showPriceText(): boolean {
    return this.data.showPriceText ?? true;
  }

  get showPricing(): boolean {
    return this.product && this.data.showPricing && !this.hideCta;
  }

  get showOriginal(): boolean {
    return (
      this.product && this.product.apiData && this.product.apiData.discountPrice < this.product.apiData.originalPrice
    );
  }

  get starRating(): number | undefined {
    return this.data.starRating ? parseInt(this.data.starRating, 10) : undefined;
  }

  get starCaption(): string {
    return this.data.starCaption;
  }

  get textContent(): string | SafeHtml | null {
    return this.data.textContent;
  }

  get cardStyles(): string {
    if (this.data.cardBackgroundColor) {
      return this.data.cardBackgroundColor;
    }
    if (this.data.styles) {
      return this.data.styles.join(' ');
    }
    return 'bg-white text-vis-regular';
  }

  get buttonData(): VisionButton {
    return {
      bgTheme: this.cardStyles,
      buttonLink: this.ctaLink,
      buttonAriaLabel: this.ctaLinkAriaLabel,
      buttonText: this.ctaLabel,
      buttonStyle: this.ctaType,
      useIcon: false,
      ctaTag: this.data.productCtaTag,
      newTab: this.data.ctaNewTab,
      nofollow: this.data.ctaNofollow,
      noopener: this.data.ctaNewTab,
      noreferrer: this.data.ctaNewTab,
      fullWidth: true,
    };
  }

  get buttonLinkData(): VisionButton {
    return {
      bgTheme: this.cardStyles,
      buttonLink: this.lmLink,
      buttonAriaLabel: this.product ? this.product.apiData.title : undefined,
      buttonText: this.lmLabel,
      buttonStyle: 'tertiary',
      buttonSize: 'small',
      useIcon: false,
      newTab: this.data.learnMoreNewTab,
      nofollow: this.data.learnMoreNofollow,
      noopener: this.data.learnMoreNewTab,
      noreferrer: this.data.learnMoreNewTab,
      fullWidth: false,
      buttonAlignment: 'center',
    };
  }

  /**
   * Displays the webinar schedule by triggering the webinar schedule service.
   *
   * @param event - The event object associated with the user interaction.
   * @param content - The content to be passed to the webinar schedule service.
   *
   * @remarks
   * This method prevents the default behavior of the event before invoking the service.
   */
  showWebinarSchedule(event: Event, content: WebinarScheduleResponse[]): void {
    event.preventDefault();
    this.webinarScheduleService.setShowWebinarSchedule(true, content);
  }

  ngOnInit(): void {
    if (this.showPriceText) {
      this.priceText = this.env.get.brandConfig.pricingText;
      if (!this.showOriginal && this.altPriceText.length > 0) {
        this.priceText = this.altPriceText;
      }
    } else {
      this.priceText = '';
      this.altPriceText = '';
    }
    this.product = this.data.xGritProduct;

    if (this.env.get.brandConfig.altPricingText) {
      this.altPriceText = this.env.get.brandConfig.altPricingText;
    }
    if (this.data.showSavings) {
      this.productSavings = this.calculateSavings();
    }
    const themeInput: ComponentTheme = {
      styles: [this.cardStyles],
      backgroundColor: this.cardStyles,
      backgroundImage: undefined,
      textColor: this.data.cardTextColor,
      columnCount: 0,
      columnDistribution: undefined,
    };
    this.theme = this.themeParser.getThemeClasses(themeInput);
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Update product when data changes (important for real-time pricing updates)
    if (changes.data && changes.data.currentValue?.xGritProduct) {
      this.product = this.data.xGritProduct;

      // Recalculate savings when product pricing changes (e.g., email capture coupon applied)
      if (this.data.showSavings) {
        this.productSavings = this.calculateSavings();
      }

      // Update pricing text based on new product data
      if (this.showPriceText) {
        this.priceText = this.env.get.brandConfig.pricingText;
        if (!this.showOriginal && this.altPriceText.length > 0) {
          this.priceText = this.altPriceText;
        }
      }
    }
  }

  calculateSavings(): string {
    const difference = this.product?.apiData.originalPrice - this.product?.apiData.discountPrice;
    if (difference >= 0) {
      if (Number.isInteger(difference)) {
        return '' + difference;
      } else {
        return difference.toFixed(2);
      }
    }
    return '';
  }

  activateModal(): void {
    this.modal.openModal({ showModal: true, modalContent: this.product.apiData.feeDescription });
  }
}
